// M-6 slice D: calls API + a session-level upload controller.
//
// List/detail are plain reads. The upload is a 3-step direct-to-R2 flow
// (presign → XHR PUT with progress → confirm) driven by a module-level
// singleton so it survives navigation away from CallUploadScreen — that's our
// pragmatic "background upload within the session" (a real IndexedDB + Service
// Worker resumable queue is deferred; Telegram WebApp on iOS kills SW anyway,
// TZ §7.2 → keep-app-open fallback). A header bar observes the singleton.
import { apiRequest } from './client'
import { MOCK } from '../dev/mock'
import type {
  CallConfirmResponse,
  CallDetailResponse,
  CallReanalyzeResponse,
  CallsListResponse,
  CallUploadUrlResponse,
} from './types'

export const MAX_CALL_BYTES = 100 * 1024 * 1024 // 100 MB (mirrors backend)
export const BG_THRESHOLD = 30 * 1024 * 1024 // ≥30 MB → offer background (TZ §3.9)
export const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/m4a',
  'audio/aac', 'audio/ogg', 'audio/opus', 'audio/wav', 'audio/x-wav', 'audio/webm',
]

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

// ── reads ───────────────────────────────────────────────────────────────────

export interface CallsFilter {
  managerId?: number | null
  from?: string | null
  to?: string | null
  scoreMin?: number | null
  scoreMax?: number | null
  page?: number
}

export async function fetchCalls(f: CallsFilter = {}): Promise<CallsListResponse> {
  const qs = new URLSearchParams()
  if (f.managerId != null) qs.set('manager_id', String(f.managerId))
  if (f.from) qs.set('from_', f.from)
  if (f.to) qs.set('to', f.to)
  if (f.scoreMin != null) qs.set('score_min', String(f.scoreMin))
  if (f.scoreMax != null) qs.set('score_max', String(f.scoreMax))
  if (f.page) qs.set('page', String(f.page))
  const q = qs.toString()
  return apiRequest<CallsListResponse>(`/api/miniapp/calls${q ? `?${q}` : ''}`)
}

export async function fetchCallDetail(callId: string): Promise<CallDetailResponse> {
  return apiRequest<CallDetailResponse>(`/api/miniapp/calls/${encodeURIComponent(callId)}`)
}

// Re-run analysis on a call whose analysis didn't finish (reuses the stored
// transcript — no STT). Owner or director. Used after the AI balance is topped up.
export async function reanalyzeCall(callId: string): Promise<CallReanalyzeResponse> {
  return apiRequest<CallReanalyzeResponse>(
    `/api/miniapp/calls/${encodeURIComponent(callId)}/reanalyze`,
    { method: 'POST', body: {} },
  )
}

// ── upload controller (singleton) ─────────────────────────────────────────────

export type UploadPhase =
  | 'idle' | 'uploading' | 'confirming' | 'analyzing' | 'done' | 'failed' | 'canceled'

export interface UploadState {
  phase: UploadPhase
  progress: number // 0..1 (PUT progress)
  fileName: string
  sizeBytes: number
  dealId: number
  client: string
  callId: string | null
  error: string | null
}

type Listener = (s: UploadState | null) => void

let _state: UploadState | null = null
let _xhr: XMLHttpRequest | null = null
let _canceled = false
const _listeners = new Set<Listener>()

function _emit(): void {
  for (const l of _listeners) l(_state)
}

function _set(patch: Partial<UploadState>): void {
  if (!_state) return
  _state = { ..._state, ...patch }
  _emit()
}

export function subscribeUpload(cb: Listener): () => void {
  _listeners.add(cb)
  cb(_state)
  return () => { _listeners.delete(cb) }
}

export function getUploadState(): UploadState | null {
  return _state
}

export function clearUpload(): void {
  if (_state && (_state.phase === 'uploading' || _state.phase === 'confirming')) return
  _state = null
  _emit()
}

export function cancelCallUpload(): void {
  _canceled = true
  try { _xhr?.abort() } catch { /* noop */ }
  _set({ phase: 'canceled' })
}

/** Kick off an upload. Replaces any finished upload; refuses to start a second
 *  in-flight one (TZ shows a single "1 загрузка" badge). Runs detached from the
 *  caller so navigation doesn't abort it. */
export function startCallUpload(opts: {
  file: Blob
  fileName: string
  dealId: number
  client: string
  comment?: string
}): boolean {
  if (_state && (_state.phase === 'uploading' || _state.phase === 'confirming')) return false
  _canceled = false
  _state = {
    phase: 'uploading',
    progress: 0,
    fileName: opts.fileName,
    sizeBytes: opts.file.size,
    dealId: opts.dealId,
    client: opts.client,
    callId: null,
    error: null,
  }
  _emit()
  void _run(opts)
  return true
}

async function _run(opts: { file: Blob; fileName: string; dealId: number; comment?: string }): Promise<void> {
  const contentType = opts.file.type || 'audio/m4a'
  try {
    if (MOCK) {
      for (let p = 0; p <= 1.0001 && !_canceled; p += 0.2) {
        _set({ progress: Math.min(1, p) })
        await delay(280)
      }
      if (_canceled) return
      _set({ phase: 'analyzing', callId: 'cq-2048', progress: 1 })
      return
    }

    // 1) presigned PUT + reserved call_quality row
    const presign = await apiRequest<CallUploadUrlResponse>('/api/miniapp/calls/upload-url', {
      method: 'POST',
      body: {
        deal_id: opts.dealId,
        content_type: contentType,
        size_bytes: opts.file.size,
        filename: opts.fileName,
      },
    })
    _set({ callId: presign.call_id })
    if (_canceled) return

    // 2) PUT straight to R2 with progress events
    await _xhrPut(presign.upload_url, opts.file, contentType)
    if (_canceled) return

    // 3) confirm → backend HEAD-checks + kicks off transcription/analysis
    _set({ phase: 'confirming' })
    const conf = await apiRequest<CallConfirmResponse>('/api/miniapp/calls/confirm', {
      method: 'POST',
      body: { call_id: presign.call_id, comment: opts.comment ?? null },
    })
    _set({ phase: 'analyzing', callId: conf.call_id })
  } catch (e: unknown) {
    if (_canceled) return
    const reason = (e as { reason?: string })?.reason || 'upload_failed'
    _set({ phase: 'failed', error: reason })
  }
}

function _xhrPut(url: string, blob: Blob, contentType: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    _xhr = xhr
    xhr.open('PUT', url, true)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) _set({ progress: ev.loaded / ev.total })
    }
    xhr.onload = () => {
      _xhr = null
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject({ status: xhr.status, reason: 'upload_failed' })
    }
    xhr.onerror = () => { _xhr = null; reject({ status: 0, reason: 'network' }) }
    xhr.onabort = () => { _xhr = null; reject({ status: 0, reason: 'canceled' }) }
    xhr.send(blob)
  })
}
